<script setup="props">
import { onMounted } from 'vue';

import { useRouter, useRoute } from 'vue-router';
import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js';

import * as shopcart from '@martyrs/src/modules/orders/store/shopcart.js';
import * as auth from '@martyrs/src/modules/auth/views/store/auth.js';

import NotificationBadge from '@martyrs/src/modules/notifications/components/elements/NotificationBadge.vue';
// Martyrs Component
import Button from '@martyrs/src/design-system/components/Button/Button.vue';
import Checkbox from '@martyrs/src/design-system/components/Checkbox/Checkbox.vue';
import LocaleSwitcher from '@martyrs/src/modules/core/views/components/partials/LocaleSwitcher.vue';
// Icons module
import IconShopcart from '@martyrs/src/modules/icons/entities/IconShopcart.vue';
import IconProfile from '@martyrs/src/modules/icons/entities/IconProfile.vue';
import IconSearch from '@martyrs/src/modules/icons/navigation/IconSearch.vue';
import IconMenu from '@martyrs/src/modules/core/views/components/icons/IconMenu.vue';
// Props
const props = defineProps({
  theme: {
    type: String,
    default: 'light',
  },
  logotype: {
    type: Object,
  },
  location: {
    type: Boolean,
    default: true,
  },
  theme_switcher: {
    type: Boolean,
    default: true,
  },
  headerClass: {
    type: [String, Array, Object],
    default: null,
  },
  showCart: {
    type: Boolean,
    default: true,
  },
  showProfile: {
    type: Boolean,
    default: true,
  },
  showNotifications: {
    type: Boolean,
    default: true,
  },
});
const router = useRouter();
const route = useRoute();
const store = useStore();
// const search = computed(() => store.products.state.search)

function openLocationPopup() {
  store.core.state.isOpenLocationPopup = true;
}
/////////////////////////////
// MOUNTED
/////////////////////////////
onMounted(() => {
  shopcart.actions.setShopcart();
});
</script>

<template>
  <header
    class="pd-thin gap-micro flex-justify-between flex-nowrap flex h-4r w-100 z-index-2"
    id="header"
    :class="[
      headerClass ?? {
        'pos-relative pos-t-0 br-b-1px t-primary br-subtle': theme === 'light',
        'pos-relative pos-t-0 br-b-1px t-inverse br-subtle': theme === 'dark',
      },
    ]"
  >
    <div class="flex-nowrap flex-v-center flex-justify-start flex gap-micro">
      <IconMenu
        :active="store.core.state.isOpenSidebar"
        :theme="theme"
        @click="store.core.state.isOpenSidebar = !store.core.state.isOpenSidebar"
      />

      <!-- LOGO -->
      <component class="cursor-pointer" v-if="logotype" :is="logotype" @click="router.push({ path: route.meta.context === 'backoffice' ? '/backoffice' : '/' })" :theme="theme" />

      <button
        class="cursor-pointer bg-transparent pd-micro radius-extra uppercase fw-semi br-2px ease-linear ws-nowrap"
        v-if="location"
        aria-label="button_location"
        :class="{
          'fill-primary br-strong t-primary hover:bg-inverse hover:t-inverse hover:fill-inverse': theme === 'light',
          'fill-inverse br-strong t-inverse hover:bg-base hover:t-primary hover:fill-primary': theme === 'dark',
        }"
        @click="openLocationPopup()"
      >
        <svg class="i-small" :fill="'inherit'" xmlns="http://www.w3.org/2000/svg" width="50" height="67" viewBox="0 0 50 67" fill="none">
          <path
            d="M25 0C11.207 0 0 11.207 0 25C0 38.793 20.832 66.668 25 66.668C29.168 66.668 50 38.793 50 25C50 11.207 38.793 0 25 0ZM25 33.332C20.418 33.332 16.668 29.582 16.668 25C16.668 20.418 20.418 16.668 25 16.668C29.582 16.668 33.332 20.418 33.332 25C33.332 29.582 29.582 33.332 25 33.332Z"
            :fill="'inherit'"
          />
        </svg>
        {{ store.core.state.position?.country ? store.core.state.position.country : 'World' }}
      </button>
    </div>

    <slot></slot>

    <div class="flex-justify-end flex-v-center flex-nowrap flex gap-micro">
      <slot name="right"></slot>

      <Button
        class="pd-zero bg-transparent"
        v-if="router.hasRoute('Search') && route.meta.header_search"
        aria-label="search"
        :class="route.meta.header_search_class"
        :submit="() => router.push({ name: 'Search' })"
        :showSucces="false"
        :showLoader="false"
      >
        <IconSearch class="i-medium" :fill="theme === 'light' ? 'rgb(var(--text-primary))' : 'rgb(var(--text-inverse))'" />
      </Button>

      <NotificationBadge
        v-if="showNotifications && auth.state.user._id"
        :buttonClass="route.meta.header_notifications_class"
        :iconClass="route.meta.header_notifications_icon_class"
        :fill="theme === 'light' ? 'rgb(var(--text-primary))' : 'rgb(var(--text-inverse))'"
      />

      <Button v-if="showCart" class="pd-zero mn-r-micro" aria-label="shopcart" :submit="a => shopcart.actions.toggleShopcart()" :counter="shopcart.getters.cartTotalAmount" :showSucces="false" :showLoader="false">
        <IconShopcart class="i-medium" :fill="theme === 'light' ? 'rgb(var(--text-primary))' : 'rgb(var(--text-inverse))'" />
      </Button>

      <Button
        v-if="showProfile"
        class="pd-zero bg-transparent"
        aria-label="profile"
        :submit="auth.state.access.status === false ? a => router.push({ name: 'Sign In' }) : a => router.push({ name: 'User Profile', params: { _id: auth.state.user._id } })"
        :showSucces="false"
        :showLoader="false"
      >
        <IconProfile class="i-medium" :fill="theme === 'light' ? 'rgb(var(--text-primary))' : 'rgb(var(--text-inverse))'" />
      </Button>

      <LocaleSwitcher />
    </div>
  </header>
</template>

<style lang="scss">
#header {
  transition:
    transform 0.3s cubic-bezier(0.08, 0.75, 0.77, 1.03),
    margin-top 0.3s cubic-bezier(0.08, 0.75, 0.77, 1.03),
    background 0.3s ease,
    border-color 0.3s ease,
    backdrop-filter 0.3s ease;

  &.header--hidden {
    transform: translateY(-100%);
    margin-top: -4rem;
  }
}

.location-button {
  &:hover {
    box-shadow: inset 0 0 0 2px rgb(var(--color-accent));
  }
}
</style>
